import { Analytics } from '@cobliteam/analytics';
import { useActivationTasks } from '@cobliteam/hooks';
import type { Permission } from '@cobliteam/permissions';
import { usePermissions } from '@cobliteam/permissions';
import { Button, Popover, Tooltip } from '@cobliteam/ui/components';
import { PopoverContent, PopoverTrigger } from '@cobliteam/ui/components/Popover';
import Icon from '@cobliteam/ui/icons';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ActivationTasksTypes } from '../ActivationTasksCommon';
import ActivationTasksSkeleton from '../ActivationTasksSkeleton';
import OngoingActivationTasks from './OngoingActivationTasks';
import { getOngoingTaskConfiguration } from './OngoingActivationTasksTypes';

const OngoingActivationTasksPopover = () => {
  const { t } = useTranslation('common');
  const { isAccessAllowed } = usePermissions();

  const { useGetUserActivationTasks, useGetActivationTasks } = useActivationTasks();
  const { data: userActivationTasks, isLoading, refetch } = useGetUserActivationTasks();

  const { data: activationTasks } = useGetActivationTasks(['ADVANCED_CONFIGURATION']);

  const ongoingActivationTasks = useMemo(() => {
    return getOngoingTaskConfiguration(activationTasks || []);
  }, []);

  const [isOpen, setIsOpen] = useState(false);

  const initialCheckedActivationTasks = useMemo(() => {
    return userActivationTasks?.filter((task) => task.finished).map((task) => task.name) as ActivationTasksTypes[];
  }, [userActivationTasks]);

  const totalActivationTasks = useMemo(() => {
    let count = 0;
    for (const category of ongoingActivationTasks) {
      for (const task of category.tasks) {
        if (isAccessAllowed(task.permissions as Permission[])) {
          count++;
        }
      }
    }
    return count;
  }, [isAccessAllowed]);

  const handleOpenActivationTasks = useCallback(() => {
    setIsOpen((prev) => {
      if (prev) {
        refetch();
      }
      return !prev;
    });

    Analytics.track('Activation Tasks - Sidebar button clicked', { type: 'ongoing' });
  }, [refetch]);

  if (totalActivationTasks === 0) {
    return null;
  }

  return (
    <Popover open={isOpen} onOpenChange={handleOpenActivationTasks}>
      <Tooltip content={t('ongoing_activation_tasks.tooltip')}>
        <PopoverTrigger asChild>
          <div className='relative mt-1.5'>
            <Button
              data-testid='app-header-ongoing-activation-tasks-button'
              className='text-default hover:text-purple-12'
              variant='surface'
              color='purple'
              size='sm'
              icon={<Icon name='book-marked' color='purple' />}
              aria-label='Ver ongoing activation tasks'
            />
          </div>
        </PopoverTrigger>
      </Tooltip>
      <PopoverContent align='end' side='right' showArrow={false} className='!px-0 flex max-h-[80vh] w-[32rem] flex-col overflow-hidden border-accessory-purple-8'>
        {isLoading ? (
          <ActivationTasksSkeleton />
        ) : (
          <OngoingActivationTasks initialCheckedActivationTasks={initialCheckedActivationTasks} onFinishOnboarding={() => setIsOpen(false)} />
        )}
      </PopoverContent>
    </Popover>
  );
};

export default OngoingActivationTasksPopover;
